/**
 * DeliveryFlowStep7 - Envelope Setup (Wizard Step 6)
 * 
 * Step 6 in the 7-step dispute wizard flow.
 * Prepare one envelope per letter/method.
 * 
 * Features:
 * - One envelope per generated letter
 * - Recipient auto-filled from selected bureau/letter
 * - Return address prefilled from Identity Anchor/profile
 * - User can review one envelope at a time
 * - User can apply return address to all envelopes
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import {
  ChevronLeft, ChevronRight, Loader2, ArrowLeft, ArrowRight, Check,
  Mail, MapPin, Building, AlertCircle, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { DisputeWizardStepper, DeliveryFlowStepper } from '../../components/disputes/DisputeWizardStepper';
import {
  extractPostalCode,
  formatCityStateZip,
  getResolvedPostalCode,
  hasValidPostalCode,
  hydrateAddressRecord,
  logAddressPipeline,
  mapCorrectAddressToProfile,
  mapUserProfileToAddress,
  toSenderAddress,
} from '../../utils/addressNormalization';
import {
  dedupeDataBreachLetters,
  dedupeDataBreachEnvelopes,
  isDataBreachLetter,
} from '../../utils/disputeEnvelopeAttachments';
import { metro2Service } from '../../services/metro2Service';
import { trackWizardStep } from '../../utils/wizardProgress';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

// Bureau mailing addresses
const BUREAU_ADDRESSES = {
  equifax: {
    name: 'Equifax Information Services LLC',
    company: 'Equifax',
    address_line_1: 'P.O. Box 740256',
    city: 'Atlanta',
    state: 'GA',
    postal_code: '30374',
    country: 'US'
  },
  experian: {
    name: 'Experian',
    company: 'Experian',
    address_line_1: 'P.O. Box 4500',
    city: 'Allen',
    state: 'TX',
    postal_code: '75013',
    country: 'US'
  },
  transunion: {
    name: 'TransUnion LLC',
    company: 'TransUnion Consumer Dispute Center',
    address_line_1: 'P.O. Box 2000',
    city: 'Chester',
    state: 'PA',
    postal_code: '19016',
    country: 'US'
  }
};

// State abbreviation mapping
const STATE_ABBREVS = {
  'alabama': 'AL', 'alaska': 'AK', 'arizona': 'AZ', 'arkansas': 'AR', 'california': 'CA',
  'colorado': 'CO', 'connecticut': 'CT', 'delaware': 'DE', 'florida': 'FL', 'georgia': 'GA',
  'hawaii': 'HI', 'idaho': 'ID', 'illinois': 'IL', 'indiana': 'IN', 'iowa': 'IA',
  'kansas': 'KS', 'kentucky': 'KY', 'louisiana': 'LA', 'maine': 'ME', 'maryland': 'MD',
  'massachusetts': 'MA', 'michigan': 'MI', 'minnesota': 'MN', 'mississippi': 'MS', 'missouri': 'MO',
  'montana': 'MT', 'nebraska': 'NE', 'nevada': 'NV', 'new hampshire': 'NH', 'new jersey': 'NJ',
  'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', 'ohio': 'OH',
  'oklahoma': 'OK', 'oregon': 'OR', 'pennsylvania': 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', 'tennessee': 'TN', 'texas': 'TX', 'utah': 'UT', 'vermont': 'VT',
  'virginia': 'VA', 'washington': 'WA', 'west virginia': 'WV', 'wisconsin': 'WI', 'wyoming': 'WY',
  'district of columbia': 'DC'
};

// Parse address string into components
// Handles formats like:
// "1457 E 101ST ST BROOKLYN, NY 11236"
// "1457 E 101ST ST, BROOKLYN, NY 11236"
// "1457 E 101ST ST APT 2 BROOKLYN, NY 11236"
// "1457 E 101ST ST, BROOKLYN" (without state/zip)
const parseAddressString = (addressStr) => {
  if (!addressStr || typeof addressStr !== 'string') return {};
  
  const str = addressStr.trim().toUpperCase();
  
  // Common city names to search for
  const commonCities = [
    'BROOKLYN', 'MOUNT VERNON', 'NEW YORK', 'BRONX', 'QUEENS', 'MANHATTAN', 'STATEN ISLAND',
    'LOS ANGELES', 'CHICAGO', 'HOUSTON', 'PHOENIX', 'PHILADELPHIA', 'SAN ANTONIO', 'SAN DIEGO',
    'DALLAS', 'SAN JOSE', 'AUSTIN', 'JACKSONVILLE', 'FORT WORTH', 'COLUMBUS', 'CHARLOTTE',
    'INDIANAPOLIS', 'SAN FRANCISCO', 'SEATTLE', 'DENVER', 'WASHINGTON', 'BOSTON', 'NASHVILLE',
    'ATLANTA', 'MIAMI', 'OAKLAND', 'MINNEAPOLIS', 'BALTIMORE', 'DETROIT', 'MEMPHIS', 'MILWAUKEE',
    'PORTLAND', 'LAS VEGAS', 'SACRAMENTO', 'KANSAS CITY', 'CLEVELAND', 'TAMPA', 'ORLANDO',
    'PITTSBURGH', 'CINCINNATI', 'RALEIGH', 'ST LOUIS', 'SALT LAKE CITY', 'NEWARK', 'JERSEY CITY',
    'LONG BEACH', 'FRESNO', 'TUCSON', 'MESA', 'OMAHA', 'TULSA', 'ARLINGTON', 'WICHITA', 'BAKERSFIELD',
    'AURORA', 'ANAHEIM', 'HONOLULU', 'RIVERSIDE', 'SANTA ANA', 'STOCKTON', 'IRVINE', 'HENDERSON',
    'NORFOLK', 'RICHMOND', 'WINSTON SALEM', 'GREENSBORO', 'DURHAM', 'FAYETTEVILLE', 'BUFFALO',
    'ROCHESTER', 'YONKERS', 'SYRACUSE', 'ALBANY', 'SCHENECTADY'
  ];
  
  // APT/SUITE patterns to detect
  const aptPatterns = /\b(APT|APARTMENT|SUITE|STE|UNIT|FL|FLOOR|#)\s*(\d+[A-Z]?|[A-Z]\d*)\b/i;
  
  // Pattern: Look for ZIP code at the end (5 digits or 5+4)
  const zipMatch = str.match(/(\d{5}(?:-\d{4})?)$/);
  
  // If no ZIP, try to parse as "street, city" or "street city" format
  if (!zipMatch) {
    let address_line_1 = '';
    let address_line_2 = '';
    let city = '';
    
    // Check for comma
    const commaIndex = str.lastIndexOf(',');
    if (commaIndex > 0) {
      const beforeComma = str.slice(0, commaIndex).trim();
      const afterComma = str.slice(commaIndex + 1).trim();
      
      // Check if afterComma is a known city
      if (commonCities.includes(afterComma)) {
        const aptMatch = beforeComma.match(aptPatterns);
        if (aptMatch) {
          const aptIndex = beforeComma.indexOf(aptMatch[0]);
          address_line_1 = beforeComma.slice(0, aptIndex).trim();
          address_line_2 = aptMatch[0];
        } else {
          address_line_1 = beforeComma;
        }
        city = afterComma;
      } else {
        address_line_1 = str;
      }
    } else {
      // No comma - search for known city in the string
      for (const cityName of commonCities) {
        const cityIdx = str.lastIndexOf(' ' + cityName);
        if (cityIdx > 0) {
          const beforeCity = str.slice(0, cityIdx).trim();
          const aptMatch = beforeCity.match(aptPatterns);
          if (aptMatch) {
            const aptIndex = beforeCity.indexOf(aptMatch[0]);
            address_line_1 = beforeCity.slice(0, aptIndex).trim();
            address_line_2 = aptMatch[0];
          } else {
            address_line_1 = beforeCity;
          }
          city = cityName;
          break;
        }
      }
      if (!city) {
        address_line_1 = str;
      }
    }
    
    return {
      address_line_1: address_line_1.trim(),
      address_line_2: address_line_2.trim(),
      city: city.trim(),
      state: '',
      postal_code: ''
    };
  }
  
  const postal_code = zipMatch[1];
  let remaining = str.slice(0, -postal_code.length).trim();
  
  // Pattern: Look for 2-letter state abbreviation before ZIP
  const stateMatch = remaining.match(/,?\s*([A-Z]{2})$/);
  if (!stateMatch) {
    return { address_line_1: remaining.trim(), postal_code };
  }
  
  const state = stateMatch[1];
  remaining = remaining.slice(0, -stateMatch[0].length).trim().replace(/,\s*$/, '');
  
  let address_line_1 = '';
  let address_line_2 = '';
  let city = '';
  
  // Check if there's a comma separating street from city
  const commaIndex = remaining.lastIndexOf(',');
  
  if (commaIndex > 0) {
    // Format: "1457 E 101ST ST, BROOKLYN"
    const beforeComma = remaining.slice(0, commaIndex).trim();
    const afterComma = remaining.slice(commaIndex + 1).trim();
    
    // Check for apt in beforeComma
    const aptMatch = beforeComma.match(aptPatterns);
    if (aptMatch) {
      const aptIndex = beforeComma.indexOf(aptMatch[0]);
      address_line_1 = beforeComma.slice(0, aptIndex).trim();
      address_line_2 = aptMatch[0];
    } else {
      address_line_1 = beforeComma;
    }
    city = afterComma;
  } else {
    // No comma - need to find city by searching for known city names
    let foundCity = false;
    
    // First check for known cities at the end of the string
    for (const cityName of commonCities) {
      if (remaining.endsWith(cityName) || remaining.endsWith(' ' + cityName)) {
        const cityIndex = remaining.lastIndexOf(cityName);
        if (cityIndex > 0) {
          const beforeCity = remaining.slice(0, cityIndex).trim();
          
          // Check for apt pattern in beforeCity
          const aptMatch = beforeCity.match(aptPatterns);
          if (aptMatch) {
            const aptIndex = beforeCity.indexOf(aptMatch[0]);
            address_line_1 = beforeCity.slice(0, aptIndex).trim();
            address_line_2 = aptMatch[0];
          } else {
            address_line_1 = beforeCity;
          }
          
          city = cityName;
          foundCity = true;
          break;
        }
      }
    }
    
    // If no known city found, try to find city by looking for the last word(s) without numbers
    if (!foundCity) {
      const words = remaining.split(/\s+/);
      let streetEndIndex = -1;
      
      // Find where street ends (last word with a number or directional suffix)
      for (let i = words.length - 1; i >= 0; i--) {
        const word = words[i];
        // Street typically ends at the last word with a number, or directional (ST, AVE, RD, etc.)
        if (/\d/.test(word) || /^(ST|AVE|RD|DR|BLVD|LN|CT|PL|WAY|CIR|TER|PKWY|HWY)$/.test(word)) {
          streetEndIndex = i;
          break;
        }
      }
      
      // Check if there's an apt pattern
      const aptMatch = remaining.match(aptPatterns);
      
      if (streetEndIndex >= 0 && streetEndIndex < words.length - 1) {
        // There are words after the street that could be city
        const potentialStreet = words.slice(0, streetEndIndex + 1).join(' ');
        const potentialCity = words.slice(streetEndIndex + 1).join(' ');
        
        if (aptMatch) {
          const aptIndex = potentialStreet.indexOf(aptMatch[0]);
          if (aptIndex > 0) {
            address_line_1 = potentialStreet.slice(0, aptIndex).trim();
            address_line_2 = aptMatch[0];
          } else {
            address_line_1 = potentialStreet;
          }
        } else {
          address_line_1 = potentialStreet;
        }
        city = potentialCity;
      } else {
        // Can't determine city, put everything in address_line_1
        if (aptMatch) {
          const aptIndex = remaining.indexOf(aptMatch[0]);
          address_line_1 = remaining.slice(0, aptIndex).trim();
          address_line_2 = aptMatch[0];
        } else {
          address_line_1 = remaining;
        }
      }
    }
  }
  
  return {
    address_line_1: address_line_1.trim(),
    address_line_2: address_line_2.trim(),
    city: city.trim(),
    state: state.trim(),
    postal_code: postal_code.trim()
  };
};

// Normalize return address from identity anchor or user profile
// Priority: Identity Anchor structured fields → Identity Anchor parsed → Profile → Empty
const normalizeReturnAddress = (identityAnchor, userProfile) => {
  let returnAddress = {
    full_name: '',
    address_line_1: '',
    address_line_2: '',
    city: '',
    state: '',
    postal_code: '',
    source: 'manual'
  };

  // Known cities for detection
  const KNOWN_CITIES = ['BROOKLYN', 'MOUNT VERNON', 'NEW YORK', 'BRONX', 'QUEENS', 'MANHATTAN', 'STATEN ISLAND',
    'LOS ANGELES', 'CHICAGO', 'HOUSTON', 'PHOENIX', 'PHILADELPHIA', 'SAN ANTONIO', 'SAN DIEGO',
    'DALLAS', 'SEATTLE', 'DENVER', 'WASHINGTON', 'BOSTON', 'ATLANTA', 'MIAMI'];

  // Helper to check if address_line_1 contains city (malformed data)
  const addressContainsCity = (addr) => {
    if (!addr) return false;
    const upper = addr.toUpperCase();
    return KNOWN_CITIES.some(city => upper.includes(city));
  };

  // Try identity anchor first - it has priority
  if (identityAnchor) {
    console.log('[NORMALIZE] Identity anchor data:', identityAnchor);
    
    // Get the name
    const name = identityAnchor.name || identityAnchor.legal_name || identityAnchor.full_name || 
                 identityAnchor.consumer_name || '';
    returnAddress.full_name = name.toUpperCase();
    
    // PRIORITY 0: Check for structured_address (from PI anchor - BEST SOURCE)
    const structuredAddr = identityAnchor.structured_address || 
                          identityAnchor.consumer_info?.structured_address;
    
    if (structuredAddr && structuredAddr.city && structuredAddr.state) {
      console.log('[NORMALIZE] Using structured_address:', structuredAddr);
      returnAddress.address_line_1 = (structuredAddr.street_address || structuredAddr.street || '').toUpperCase();
      returnAddress.address_line_2 = (structuredAddr.unit || structuredAddr.address_line_2 || '').toUpperCase();
      returnAddress.city = (structuredAddr.city || '').toUpperCase();
      returnAddress.state = (structuredAddr.state || '').toUpperCase();
      returnAddress.postal_code = extractPostalCode(structuredAddr);
      returnAddress.source = 'identity_anchor';
      
      // Normalize state
      if (returnAddress.state) {
        const stateLower = returnAddress.state.toLowerCase().trim();
        if (STATE_ABBREVS[stateLower]) {
          returnAddress.state = STATE_ABBREVS[stateLower];
        }
        returnAddress.state = returnAddress.state.toUpperCase().slice(0, 2);
      }
      
      console.log('[RETURN ADDRESS NORMALIZED - from structured_address]', returnAddress);
      return returnAddress;
    }
    
    // PRIORITY 1: Check if we have COMPLETE structured address directly on anchor
    const hasValidStructured = (
      (identityAnchor.address_line_1 || identityAnchor.street) &&
      identityAnchor.city &&
      identityAnchor.state &&
      extractPostalCode(identityAnchor) &&
      !addressContainsCity(identityAnchor.address_line_1) // address_line_1 should NOT contain city name
    );
    
    if (hasValidStructured) {
      console.log('[NORMALIZE] Using valid structured fields on anchor');
      returnAddress.address_line_1 = (identityAnchor.address_line_1 || identityAnchor.street || '').toUpperCase();
      returnAddress.address_line_2 = (identityAnchor.address_line_2 || identityAnchor.apt || identityAnchor.unit || '').toUpperCase();
      returnAddress.city = (identityAnchor.city || '').toUpperCase();
      returnAddress.state = (identityAnchor.state || '').toUpperCase();
      returnAddress.postal_code = extractPostalCode(identityAnchor);
      returnAddress.source = 'identity_anchor';
    } else {
      // Structured fields are missing or malformed - need to parse from raw address string
      let rawAddress = null;
      
      // Priority: current_address > address string > full_address > address_line_1 (if contains city)
      if (identityAnchor.current_address && typeof identityAnchor.current_address === 'string') {
        rawAddress = identityAnchor.current_address;
      } else if (identityAnchor.address && typeof identityAnchor.address === 'string') {
        rawAddress = identityAnchor.address;
      } else if (identityAnchor.full_address && typeof identityAnchor.full_address === 'string') {
        rawAddress = identityAnchor.full_address;
      } else if (identityAnchor.address_line_1 && (!identityAnchor.city || addressContainsCity(identityAnchor.address_line_1))) {
        // address_line_1 exists but city is missing OR address_line_1 contains city (malformed)
        let reconstructed = identityAnchor.address_line_1;
        if (identityAnchor.state && !reconstructed.includes(identityAnchor.state)) {
          reconstructed += ', ' + identityAnchor.state;
        }
        if (identityAnchor.zip && !reconstructed.includes(identityAnchor.zip)) {
          reconstructed += ' ' + identityAnchor.zip;
        }
        rawAddress = reconstructed;
      }
      
      if (rawAddress) {
        console.log('[NORMALIZE] Parsing raw address:', rawAddress);
        const parsed = parseAddressString(rawAddress);
        console.log('[NORMALIZE] Parse result:', parsed);
        
        returnAddress.address_line_1 = (parsed.address_line_1 || '').toUpperCase();
        returnAddress.address_line_2 = (parsed.address_line_2 || '').toUpperCase();
        returnAddress.city = (parsed.city || '').toUpperCase();
        returnAddress.state = (parsed.state || '').toUpperCase();
        returnAddress.postal_code = parsed.postal_code || '';
        returnAddress.source = 'identity_anchor';
      }
    }
  }

  // Fall back to user profile if identity anchor fields are missing
  if ((!returnAddress.city || !returnAddress.state || !returnAddress.postal_code || !returnAddress.address_line_1) && userProfile) {
    console.log('[NORMALIZE] Falling back to user profile:', userProfile);
    if (!returnAddress.full_name) {
      returnAddress.full_name = `${userProfile.first_name || ''} ${userProfile.last_name || ''}`.trim().toUpperCase() || 
                                (userProfile.name || '').toUpperCase();
    }
    
    // Check for structured profile address
    const profileAddr = userProfile.address || userProfile;
    if (typeof profileAddr === 'object' && profileAddr !== null) {
      if (!returnAddress.address_line_1) {
        returnAddress.address_line_1 = (profileAddr.street || profileAddr.address_line_1 || profileAddr.line1 || '').toUpperCase();
      }
      if (!returnAddress.address_line_2) {
        returnAddress.address_line_2 = (profileAddr.address_line_2 || profileAddr.apt || profileAddr.unit || profileAddr.line2 || '').toUpperCase();
      }
      if (!returnAddress.city) {
        returnAddress.city = (profileAddr.city || '').toUpperCase();
      }
      if (!returnAddress.state) {
        returnAddress.state = (profileAddr.state || '').toUpperCase();
      }
      if (!returnAddress.postal_code) {
        returnAddress.postal_code = extractPostalCode(profileAddr) || extractPostalCode(userProfile);
      }
      if (returnAddress.source === 'manual') {
        returnAddress.source = 'profile';
      }
    }
    // Parse profile address string
    else if (typeof profileAddr === 'string' && profileAddr.length > 0) {
      const parsed = parseAddressString(profileAddr);
      if (!returnAddress.address_line_1) returnAddress.address_line_1 = (parsed.address_line_1 || '').toUpperCase();
      if (!returnAddress.address_line_2) returnAddress.address_line_2 = (parsed.address_line_2 || '').toUpperCase();
      if (!returnAddress.city) returnAddress.city = (parsed.city || '').toUpperCase();
      if (!returnAddress.state) returnAddress.state = (parsed.state || '').toUpperCase();
      if (!returnAddress.postal_code) returnAddress.postal_code = parsed.postal_code || '';
      if (returnAddress.source === 'manual') {
        returnAddress.source = 'profile';
      }
    }
  }

  // Normalize state to 2-letter abbreviation
  if (returnAddress.state) {
    const stateLower = returnAddress.state.toLowerCase().trim();
    if (STATE_ABBREVS[stateLower]) {
      returnAddress.state = STATE_ABBREVS[stateLower];
    }
    returnAddress.state = returnAddress.state.toUpperCase().slice(0, 2);
  }

  console.log('[RETURN ADDRESS NORMALIZED]', {
    source: returnAddress.source,
    result: returnAddress
  });

  return returnAddress;
};

// ============================================
// MAIN COMPONENT: DeliveryFlowStep7
// ============================================
const DeliveryFlowStep7 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token, getAuthHeaders } = useAuth();

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [letters, setLetters] = useState([]);
  const [envelopes, setEnvelopes] = useState([]);
  const [currentEnvelopeIndex, setCurrentEnvelopeIndex] = useState(0);
  const [senderAddresses, setSenderAddresses] = useState({});
  const [applyToAll, setApplyToAll] = useState(true);
  const [identityAnchor, setIdentityAnchor] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [packageId, setPackageId] = useState(null);
  const [packageIdentitySupport, setPackageIdentitySupport] = useState({});
  const [perLetterAttachments, setPerLetterAttachments] = useState({});
  const [packageSharedAttachments, setPackageSharedAttachments] = useState([]);
  const [dataBreachProof, setDataBreachProof] = useState(null);
  const [ssnApprovals, setSsnApprovals] = useState({});
  
  // NEW: Address options state for selectable cards
  const [addressOptions, setAddressOptions] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);

  const currentEnvelope = envelopes[currentEnvelopeIndex];
  const currentSender = senderAddresses[currentEnvelope?.envelope_id] || {};

  // ============================================
  // BUILD ADDRESS OPTIONS FROM IDENTITY ANCHOR AND PROFILE
  // ============================================
  const buildAddressOptions = (anchorData, profileData) => {
    const options = [];
    
    // PRIORITY 1: Identity Anchor address (from PI anchor selection)
    if (anchorData) {
      const structuredAddr = anchorData.structured_address || {};
      const normalizedAddr = anchorData.normalized_address || {};
      
      // Try multiple sources for each field - PROPERLY SEPARATE street and unit
      // Check structured_address, normalized_address, AND direct anchorData fields
      let rawAddressLine1 = structuredAddr.street_address || 
                           structuredAddr.address_line_1 || 
                           normalizedAddr.street_address ||
                           normalizedAddr.address_line_1 ||
                           anchorData.address_line_1 || 
                           anchorData.street ||
                           '';
      let address_line_2 = structuredAddr.unit || 
                           structuredAddr.address_line_2 || 
                           normalizedAddr.unit ||
                           normalizedAddr.address_line_2 ||
                           anchorData.address_line_2 ||
                           anchorData.unit ||
                           '';
      // CRITICAL: Check ALL possible sources for city/state/zip
      let city = structuredAddr.city || 
                 normalizedAddr.city || 
                 anchorData.city || 
                 '';
      let state = structuredAddr.state || 
                  normalizedAddr.state || 
                  anchorData.state || 
                  '';
      let zip =
        extractPostalCode(structuredAddr) ||
        extractPostalCode(normalizedAddr) ||
        extractPostalCode(anchorData) ||
        '';
      
      // CRITICAL FIX: If address_line_1 contains a unit pattern (FL 2, APT 2, etc), extract it
      // Pattern: "1457 E 101ST ST FL 2" should become street="1457 E 101ST ST", unit="FL 2"
      let address_line_1 = rawAddressLine1;
      if (!address_line_2 && rawAddressLine1) {
        const unitPatterns = [
          /\s+(FL\s*\d+[A-Z]?)\s*$/i,         // FL 2, FL 2A
          /\s+(FLOOR\s*\d+[A-Z]?)\s*$/i,      // FLOOR 2
          /\s+(APT\.?\s*[A-Z0-9]+)\s*$/i,     // APT 2, APT 2A, APT. B
          /\s+(UNIT\s*[A-Z0-9]+)\s*$/i,       // UNIT 2, UNIT A
          /\s+(STE\.?\s*[A-Z0-9]+)\s*$/i,     // STE 100, STE. A
          /\s+(SUITE\s*[A-Z0-9]+)\s*$/i,      // SUITE 100
          /\s+(#\s*[A-Z0-9]+)\s*$/i,          // #2, # 2A
        ];
        
        for (const pattern of unitPatterns) {
          const match = rawAddressLine1.match(pattern);
          if (match) {
            address_line_2 = match[1].trim();
            address_line_1 = rawAddressLine1.replace(pattern, '').trim();
            break;
          }
        }
      }
      
      // If still missing city/state/zip, try to parse from various string sources
      if (address_line_1 && (!city || !state || !zip)) {
        // Try multiple string sources that might contain the full address
        let rawAddress = anchorData.current_address || 
                        anchorData.address ||
                        anchorData.full_address ||
                        anchorData.correct_value ||
                        normalizedAddr.full_address ||
                        structuredAddr.full_address ||
                        '';
        
        if (rawAddress) {
          const parsed = parseAddressString(rawAddress);
          if (!city && parsed.city) city = parsed.city;
          if (!state && parsed.state) state = parsed.state;
          if (!zip && parsed.postal_code) zip = parsed.postal_code;
          // Also check if parser found unit we missed
          if (!address_line_2 && parsed.address_line_2) {
            address_line_2 = parsed.address_line_2;
            // If parser also extracted clean street, use it
            if (parsed.address_line_1 && parsed.address_line_1.length < address_line_1.length) {
              address_line_1 = parsed.address_line_1;
            }
          }
        }
      }
      
      // EXTRA FALLBACK: If we have address_line_1 and city but STILL missing state/zip,
      // try to find state/zip in any string field that might contain them
      if (city && (!state || !zip)) {
        // Look for state/zip pattern in any anchor string fields
        const checkFields = [
          anchorData.raw_address,
          anchorData.original_address,
          anchorData.full_address_string,
          anchorData.address_text,
          anchorData.anchor_value,
          anchorData.correct_value,
          // Also try concatenating known fields
          address_line_1 + (city ? ', ' + city : ''),
        ];
        
        for (const field of checkFields) {
          if (field && typeof field === 'string') {
            // Look for state abbreviation pattern: ", NY " or " NY "
            const stateMatch = field.match(/[,\s]([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/i);
            if (stateMatch) {
              if (!state) state = stateMatch[1].toUpperCase();
              if (!zip) zip = stateMatch[2];
              console.log('[STEP 7] Found state/zip in field:', { field: field.substring(0, 50), state, zip });
              break;
            }
            // Also try just state pattern
            if (!state) {
              const justStateMatch = field.match(/[,\s]([A-Z]{2})\s*$/i);
              if (justStateMatch) {
                state = justStateMatch[1].toUpperCase();
              }
            }
          }
        }
        
        // If STILL no state, and city is known, try to infer state from city
        // (only for well-known cities)
        if (city && !state) {
          const cityToState = {
            'NEW YORK': 'NY',
            'MOUNT VERNON': 'NY',  // Mount Vernon is in NY
            'LOS ANGELES': 'CA',
            'CHICAGO': 'IL',
            'HOUSTON': 'TX',
            'BROOKLYN': 'NY',
            'BRONX': 'NY',
            'QUEENS': 'NY',
            'MANHATTAN': 'NY',
            'STATEN ISLAND': 'NY',
          };
          const inferredState = cityToState[city.toUpperCase()];
          if (inferredState) {
            state = inferredState;
            console.log('[STEP 7] Inferred state from city:', { city, state: inferredState });
          }
        }
      }
      
      // CRITICAL FIX: If address_line_1 contains city at the end (like "660 LOCUST ST D3 MOUNT VERNON"),
      // and city is the same as what's embedded, we should NOT show city twice
      // Also check if city is embedded in address_line_1 - if so, extract properly
      if (city && address_line_1) {
        const addressUpper = address_line_1.toUpperCase();
        const cityUpper = city.toUpperCase();
        
        // If address_line_1 ends with the city name, extract it out
        if (addressUpper.endsWith(cityUpper)) {
          // Extract the city from address_line_1
          const newAddr = address_line_1.slice(0, -cityUpper.length).trim();
          // Remove trailing comma or space
          address_line_1 = newAddr.replace(/[,\s]+$/, '').trim();
          console.log('[STEP 7] Extracted embedded city from address_line_1:', { original: addressUpper, cleaned: address_line_1, city: cityUpper });
        }
      }
      
      // FINAL ROBUST FALLBACK: Look for state/zip in the correct_value field (full PI anchor value)
      // This is often the most reliable source as it contains the complete address
      if (!state || !zip) {
        const valuesToCheck = [
          anchorData.correct_value,
          anchorData.anchor_value, 
          anchorData.raw_value,
          anchorData.pi_value,
          anchorData.selected_value,
          anchorData.full_value,
          anchorData.value,
          // Build a combined string from all known fields
          `${rawAddressLine1 || ''} ${city || ''}, ${state || ''} ${zip || ''}`.trim()
        ].filter(Boolean);
        
        for (const val of valuesToCheck) {
          if (typeof val === 'string' && val.length > 10) {
            // Parse the full address string to extract components
            const fullParsed = parseAddressString(val);
            if (!state && fullParsed.state) {
              state = fullParsed.state;
              console.log('[STEP 7] Extracted state from correct_value/anchor_value:', val.substring(0, 50), '->', state);
            }
            if (!zip && fullParsed.postal_code) {
              zip = fullParsed.postal_code;
              console.log('[STEP 7] Extracted zip from correct_value/anchor_value:', val.substring(0, 50), '->', zip);
            }
            if (state && zip) break;
          }
        }
      }
      
      // VERY LAST RESORT: Scan ALL string properties on anchorData for state/zip pattern
      if (!state || !zip) {
        const scanForStateZip = (obj, depth = 0) => {
          if (depth > 2 || !obj || typeof obj !== 'object') return { state: null, zip: null };
          for (const [key, val] of Object.entries(obj)) {
            if (typeof val === 'string' && val.length > 5) {
              // Look for pattern like ", NY 10552" or " NY 10552"
              const match = val.match(/[,\s]([A-Z]{2})\s+(\d{5}(?:-\d{4})?)/i);
              if (match) {
                return { state: match[1].toUpperCase(), zip: match[2] };
              }
            } else if (typeof val === 'object' && val !== null) {
              const nested = scanForStateZip(val, depth + 1);
              if (nested.state && nested.zip) return nested;
            }
          }
          return { state: null, zip: null };
        };
        
        const scanned = scanForStateZip(anchorData);
        if (!state && scanned.state) {
          state = scanned.state;
          console.log('[STEP 7] Found state via deep scan:', state);
        }
        if (!zip && scanned.zip) {
          zip = scanned.zip;
          console.log('[STEP 7] Found zip via deep scan:', zip);
        }
      }
      
      // Build the final values with fallbacks
      const finalCity = (city || profileData?.city || '').toUpperCase().trim();
      const finalState = (state || profileData?.state || '').toUpperCase().slice(0, 2).trim();
      const finalZip =
        extractPostalCode({ zip, postal_code: zip }) ||
        extractPostalCode(profileData) ||
        extractPostalCode(anchorData?.current_address) ||
        extractPostalCode(anchorData?.correct_value) ||
        '';

      const isIncomplete =
        !finalState ||
        finalState.length !== 2 ||
        !hasValidPostalCode({ zip: finalZip, postal_code: finalZip });

      const anchorOption = hydrateAddressRecord(
        {
          id: 'identity_anchor',
          full_name:
            anchorData.name ||
            anchorData.legal_name ||
            anchorData.full_name ||
            profileData?.full_name ||
            '',
          address_line_1,
          address_line_2,
          city: finalCity,
          state: finalState,
          zip: finalZip,
          source: 'identity_anchor',
          label: 'Client Profile',
          is_primary: true,
          isIncomplete,
        },
        profileData || {}
      );
      
      console.log('[STEP 7] Built anchorOption with profileData fallback:', anchorOption, { isIncomplete });
      
      // Add if we have at least street address
      if (anchorOption.address_line_1) {
        options.push(anchorOption);
      }
    }
    
    // PRIORITY 2: Previously confirmed / normalized address
    if (anchorData?.normalized_address) {
      const norm = anchorData.normalized_address;
      
      // Also apply unit extraction logic here
      let street = norm.street_address || norm.address_line_1 || '';
      let unit = norm.unit || norm.address_line_2 || '';
      
      // Extract unit if embedded in street
      if (!unit && street) {
        const unitPatterns = [
          /\s+(FL\s*\d+[A-Z]?)\s*$/i,
          /\s+(FLOOR\s*\d+[A-Z]?)\s*$/i,
          /\s+(APT\.?\s*[A-Z0-9]+)\s*$/i,
          /\s+(UNIT\s*[A-Z0-9]+)\s*$/i,
          /\s+(STE\.?\s*[A-Z0-9]+)\s*$/i,
          /\s+(SUITE\s*[A-Z0-9]+)\s*$/i,
          /\s+(#\s*[A-Z0-9]+)\s*$/i,
        ];
        for (const pattern of unitPatterns) {
          const match = street.match(pattern);
          if (match) {
            unit = match[1].trim();
            street = street.replace(pattern, '').trim();
            break;
          }
        }
      }
      
      const prevOption = hydrateAddressRecord(
        {
          id: 'previously_confirmed',
          full_name: anchorData.name || anchorData.legal_name || '',
          address_line_1: street,
          address_line_2: unit,
          city: norm.city,
          state: norm.state,
          zip: extractPostalCode(norm),
          source: 'previously_confirmed',
          label: 'Previously Confirmed',
          is_primary: false,
        },
        profileData || {}
      );
      
      // Only add if different from first option and has meaningful data
      if (prevOption.address_line_1 && prevOption.city) {
        if (options.length === 0 || prevOption.address_line_1 !== options[0].address_line_1) {
          options.push(prevOption);
        }
      }
    }
    
    // PRIORITY 3: User profile address
    if (profileData && (profileData.address_line_1 || profileData.street)) {
      const profileOption = hydrateAddressRecord(
        {
          id: 'profile',
          full_name: profileData.full_name || profileData.name || '',
          address_line_1: profileData.address_line_1 || profileData.street || '',
          address_line_2: profileData.address_line_2 || profileData.unit || '',
          city: profileData.city,
          state: profileData.state,
          zip: extractPostalCode(profileData),
          source: 'profile',
          label: 'User Profile',
          is_primary: false,
        },
        profileData
      );
      
      // Only add if different
      if (profileOption.address_line_1 && !options.some(o => o.address_line_1 === profileOption.address_line_1)) {
        options.push(profileOption);
      }
    }
    
    return options;
  };

  // ============================================
  // HANDLE ADDRESS CARD SELECTION - THE KEY FIX
  // ============================================
  const handleUseAddress = (address) => {
    const normalized = toSenderAddress(
      { ...address, selected_address_id: address.id },
      userProfile || {}
    );

    logAddressPipeline('selectedAddress', {
      selectedAddress: address,
      hydratedAddress: normalized,
      validationAddress: normalized,
    });
    
    // Save selected address ID
    setSelectedAddressId(address.id);
    
    // CRITICAL: Apply to current envelope and all envelopes if toggle is ON
    if (applyToAll) {
      const allUpdated = {};
      envelopes.forEach(env => {
        allUpdated[env.envelope_id] = { ...normalized };
      });
      setSenderAddresses(allUpdated);
      console.log('[ENVELOPE RETURN ADDRESSES - ALL UPDATED]', allUpdated);
    } else {
      setSenderAddresses(prev => ({
        ...prev,
        [currentEnvelope?.envelope_id]: { ...normalized }
      }));
    }
    
    console.log('[SELECTED RETURN ADDRESS SAVED]', {
      selected_return_address_id: address.id,
      selected_return_address_source: normalized.source,
      selected_return_address_normalized: normalized
    });
  };

  // ============================================
  // INITIALIZE
  // ============================================
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setError(null);
      try {
        // Get data from previous step state
        const stepData = location.state || {};
        const letterPackage = stepData.letterPackage || stepData.letters;
        const pkgId = stepData.packageId || stepData.package_id;
        let anchorData = stepData.identityAnchor || stepData.selectedAnchors;
        const identitySupport = stepData.packageIdentitySupport || {};
        const letterAttachments = stepData.perLetterAttachments || {};
        const sharedAttachments = stepData.packageSharedAttachments || [];
        const breachProof = stepData.dataBreachProof;
        const ssnApprovalState = stepData.ssnApprovals || {};
        const persistedSenderAddresses = stepData.senderAddresses || null;

        if (!letterPackage) {
          throw new Error('No letter package provided. Please complete previous steps first.');
        }

        // Set state
        const lettersArrayRaw = letterPackage.letters || letterPackage;
        const lettersArrayNormalized = Array.isArray(lettersArrayRaw)
          ? lettersArrayRaw
          : [lettersArrayRaw];
        const { letters: lettersArray, removedCount: duplicateLettersRemoved } = dedupeDataBreachLetters(
          lettersArrayNormalized,
          { packageId: pkgId }
        );
        if (duplicateLettersRemoved > 0) {
          console.warn('[STEP 7] Removed duplicate Data Breach letters before envelope setup', {
            duplicateLettersRemoved,
            before: lettersArrayNormalized.length,
            after: lettersArray.length,
          });
        }
        setLetters(lettersArray);
        setPackageId(pkgId);
        setIdentityAnchor(anchorData);
        setPackageIdentitySupport(identitySupport);
        setPerLetterAttachments(letterAttachments);
        setPackageSharedAttachments(sharedAttachments);
        setDataBreachProof(breachProof);
        setSsnApprovals(ssnApprovalState);

        // CRITICAL FIX: Fetch user profile AND correct_address from backend to get complete address
        let profileData = null;
        
        // Try to fetch correct_address (most reliable source if user has confirmed address)
        try {
          const correctAddrRes = await axios.get(`${API}/address/correct-address`, {
            headers: getAuthHeaders(),
            withCredentials: true
          });
          
          if (correctAddrRes.data?.success && correctAddrRes.data?.correct_address) {
            profileData = mapCorrectAddressToProfile(
              correctAddrRes.data.correct_address,
              user?.full_name || user?.name || ''
            );
            logAddressPipeline('savedAddress-correct_address', {
              savedAddress: correctAddrRes.data.correct_address,
              hydratedAddress: profileData,
            });
            setUserProfile(profileData);
          }
        } catch (e) {
          console.log('[STEP 7] No correct_address found, trying user profile:', e.response?.data?.detail || e.message);
        }
        
        // Fallback: Fetch from user profile mailing_address
        if (!profileData) {
          try {
            const profileRes = await axios.get(`${API}/users/profile`, {
              headers: getAuthHeaders(),
              withCredentials: true
            });
            
            profileData = mapUserProfileToAddress(
              profileRes.data,
              user?.full_name || user?.name || ''
            );
            if (profileData) {
              logAddressPipeline('savedAddress-user_profile', {
                savedAddress: profileRes.data,
                hydratedAddress: profileData,
              });
              setUserProfile(profileData);
            }
          } catch (e) {
            console.log('[STEP 7] Could not fetch user profile, trying saved-addresses:', e);
            // Fallback to saved-addresses endpoint
            try {
              const savedRes = await axios.get(`${API}/delivery/saved-addresses`, {
                headers: getAuthHeaders(),
                withCredentials: true
              });
              if (savedRes.data?.addresses?.length > 0) {
                const primary =
                  savedRes.data.addresses.find((a) => a.id === 'primary') ||
                  savedRes.data.addresses[0];
                const savedRaw = primary?.address || primary;
                profileData = hydrateAddressRecord(savedRaw, {
                  full_name: user?.full_name || user?.name || '',
                  source: 'saved_addresses',
                });
                logAddressPipeline('savedAddress-api', {
                  savedAddress: savedRaw,
                  hydratedAddress: profileData,
                });
                setUserProfile(profileData);
              }
            } catch (e2) {
              console.log('[STEP 7] Could not fetch saved addresses either');
            }
          }
        }

        // Generate envelopes (one per letter)
        const generatedEnvelopesRaw = lettersArray.map((letter, idx) => {
          // Get letter ID from multiple possible sources
          const letterId = letter.id || letter.letter_id || letter._id || letter.snapshot_id || 
                           letter.package_id || `letter_${letter.bureau?.toLowerCase()}_${idx}_${Date.now()}`;
          
          return {
            envelope_id: letterId || `env_${idx}_${Date.now()}`,
            letter_id: letterId,
            package_id: letter.package_id || pkgId,
            bureau: letter.bureau,
            method: letter.method || letter.package_subtype || letter.strategy || 'factual',
            envelope_type: isDataBreachLetter(letter) ? 'data_breach' : 'regular_dispute',
            recipient_address: BUREAU_ADDRESSES[letter.bureau?.toLowerCase()] || null,
            letters: [letter],
            page_count: (letter.page_count || 1) + 2, // Letter + attachments estimate
          };
        });

        const {
          envelopes: generatedEnvelopes,
          removedCount: duplicateEnvelopesRemoved,
        } = dedupeDataBreachEnvelopes(generatedEnvelopesRaw, { packageId: pkgId });
        if (duplicateEnvelopesRemoved > 0) {
          console.warn('[STEP 7] Removed duplicate Data Breach envelopes', {
            duplicateEnvelopesRemoved,
            before: generatedEnvelopesRaw.length,
            after: generatedEnvelopes.length,
          });
          toast.info(
            `Removed ${duplicateEnvelopesRemoved} duplicate Data Breach envelope(s). Total: ${generatedEnvelopes.length}.`,
            { duration: 5000 }
          );
        }

        setEnvelopes(generatedEnvelopes);
        console.log('[STEP 7 INIT] Generated envelopes with letter_ids:', 
          generatedEnvelopes.map(e => ({ envelope_id: e.envelope_id, letter_id: e.letter_id, bureau: e.bureau }))
        );

        const profilePostal = extractPostalCode(profileData);
        if (profilePostal && anchorData && !extractPostalCode(anchorData)) {
          anchorData = {
            ...anchorData,
            zip: profilePostal,
            postal_code: profilePostal,
            structured_address: {
              ...(anchorData.structured_address || {}),
              zip: profilePostal,
              postal_code: profilePostal,
            },
          };
        }

        const options = buildAddressOptions(anchorData, profileData).map((opt) =>
          hydrateAddressRecord(opt, profileData || {})
        );
        setAddressOptions(options);

        const applySenderToEnvelopes = (senderRecord) => {
          const normalized = toSenderAddress(senderRecord, profileData || {});
          const initialAddresses = {};
          generatedEnvelopes.forEach((env) => {
            initialAddresses[env.envelope_id] = { ...normalized };
          });
          setSenderAddresses(initialAddresses);
          logAddressPipeline('validationAddress', {
            hydratedAddress: normalized,
            validationAddress: normalized,
          });
          return normalized;
        };

        const hasPersistedSenders =
          persistedSenderAddresses &&
          Object.keys(persistedSenderAddresses).length > 0;

        if (hasPersistedSenders) {
          const firstEnv = generatedEnvelopes[0];
          const rawPersisted =
            persistedSenderAddresses[firstEnv?.envelope_id] ||
            persistedSenderAddresses[firstEnv?.letter_id] ||
            Object.values(persistedSenderAddresses)[0];

          const restored = applySenderToEnvelopes(rawPersisted);
          if (restored.selected_address_id) {
            setSelectedAddressId(restored.selected_address_id);
          } else if (options.length > 0) {
            setSelectedAddressId(options[0].id);
          }
        } else if (options.length > 0) {
          const primaryOption = options[0];
          setSelectedAddressId(primaryOption.id);
          applySenderToEnvelopes({
            ...primaryOption,
            selected_address_id: primaryOption.id,
          });
        } else {
          const normalizedAddr = normalizeReturnAddress(anchorData, profileData);
          applySenderToEnvelopes(normalizedAddr);
        }

        setLoading(false);
      } catch (err) {
        console.error('Init error:', err);
        setError(err.message || 'Failed to load envelope setup');
        setLoading(false);
      }
    };

    init();
  }, [location, getAuthHeaders]);

  // Update sender address and optionally apply to all - ONLY if no address selected yet
  useEffect(() => {
    // CRITICAL: Don't overwrite if we already have a selected address
    if (selectedAddressId) {
      console.log('[STEP 7] Skip address update - already have selected address:', selectedAddressId);
      return;
    }
    
    if (userProfile && identityAnchor && envelopes.length > 0) {
      const normalizedAddr = normalizeReturnAddress(identityAnchor, userProfile);
      
      // Only update if addresses are still empty
      const needsUpdate = Object.values(senderAddresses).some(
        addr => !addr.full_name || !addr.address_line_1
      );

      if (needsUpdate) {
        const updatedAddresses = { ...senderAddresses };
        envelopes.forEach(env => {
          if (!updatedAddresses[env.envelope_id]?.full_name || !updatedAddresses[env.envelope_id]?.address_line_1) {
            updatedAddresses[env.envelope_id] = { ...normalizedAddr };
          }
        });
        setSenderAddresses(updatedAddresses);
      }
    }
  }, [userProfile, identityAnchor, envelopes, selectedAddressId]);

  // Handle sender field change
  const handleSenderChange = (field, value) => {
    const envId = currentEnvelope?.envelope_id;
    if (!envId) return;

    // Mark as manual when user edits
    const updated = {
      ...senderAddresses[envId],
      [field]: value,
      source: 'manual',
    };
    if (field === 'postal_code') {
      updated.zip = value;
    }
    
    if (applyToAll) {
      // Apply to all envelopes
      const allUpdated = { ...senderAddresses };
      envelopes.forEach(env => {
        allUpdated[env.envelope_id] = { ...updated };
      });
      setSenderAddresses(allUpdated);
    } else {
      setSenderAddresses(prev => ({ ...prev, [envId]: updated }));
    }
  };

  // Validate addresses - Full return address required for PostGrid
  const validateAddresses = () => {
    const incomplete = envelopes.filter(env => {
      const addr = senderAddresses[env.envelope_id] || {};
      // Required fields for PostGrid envelope: full_name, address_line_1, city, state, postal_code
      // address_line_2 is optional (for apt/suite/floor)
      const postal = getResolvedPostalCode(addr);
      return (
        !addr.full_name?.trim() ||
        !addr.address_line_1?.trim() ||
        !addr.city?.trim() ||
        !addr.state?.trim() ||
        !postal ||
        !hasValidPostalCode({ postal_code: postal, zip: postal }) ||
        addr.state?.trim()?.length !== 2
      );
    });
    return incomplete;
  };

  const buildWizardNavState = () => ({
    letterPackage: { letters },
    packageId,
    package_id: packageId,
    identityAnchor,
    packageIdentitySupport,
    perLetterAttachments,
    packageSharedAttachments,
    dataBreachProof,
    ssnApprovals,
    senderAddresses,
    envelopes,
    appliedSignatures: location.state?.appliedSignatures,
    signatureSettings: location.state?.signatureSettings,
    roundId: location.state?.roundId,
  });

  // Navigation
  const handleBack = () => {
    navigate('/dispute-wizard/final-package', {
      state: buildWizardNavState(),
    });
  };

  const handleContinue = () => {
    const incomplete = validateAddresses();
    if (incomplete.length > 0) {
      // Find specific missing fields
      const firstIncomplete = incomplete[0];
      const addr = senderAddresses[firstIncomplete.envelope_id] || {};
      
      if (!addr.full_name?.trim()) {
        toast.error('Return address name is required');
      } else if (!addr.address_line_1?.trim()) {
        toast.error('Return address street is required');
      } else if (!addr.city?.trim()) {
        toast.error('Return address city is required');
      } else if (!addr.state?.trim()) {
        toast.error('Return address state is required');
      } else if (addr.state?.trim()?.length !== 2) {
        toast.error('State must be a 2-letter abbreviation (e.g., NY, CA)');
      } else if (!getResolvedPostalCode(addr)) {
        toast.error('Return address ZIP code is required');
      } else {
        toast.error(`Please complete return addresses for all ${incomplete.length} envelope(s)`);
      }
      return;
    }

    trackWizardStep({
      metro2Service,
      roundId: location.state?.roundId,
      token,
      currentStep: 'send',
      lastCompletedStep: 'envelope_setup',
      packageId,
      wizardStatus: 'pending_send',
      envelopeSettings: senderAddresses,
      navigationState: buildWizardNavState(),
    });

    navigate('/dispute-wizard/step8-send', {
      state: buildWizardNavState(),
    });
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17] flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-emerald-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-2">Loading Envelope Setup</h3>
          <p className="text-stone-600 dark:text-[#C7DDD0]">Preparing your envelopes...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    // Ensure error is always a string for React rendering
    const errorMessage = typeof error === 'string' 
      ? error 
      : (error?.message || error?.detail || JSON.stringify(error));
    
    return (
      <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17] flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="text-red-500 mx-auto mb-4" />
          <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0] mb-2">Error</h3>
          <p className="text-stone-600 dark:text-[#C7DDD0] mb-4">{errorMessage}</p>
          <Button onClick={() => navigate(-1)} variant="outline">
            <ArrowLeft size={16} className="mr-2" /> Go Back
          </Button>
        </div>
      </div>
    );
  }

  const recipient = currentEnvelope?.recipient_address;

  return (
    <div className="min-h-screen bg-[#F7F9F8] dark:bg-[#0a1f17]" data-testid="delivery-flow-step7">
      {/* Full 8-Step Wizard Stepper */}
      <DisputeWizardStepper currentStep={6} compact={true} />
      
      {/* Header */}
      <div className="bg-white dark:bg-[rgba(10,38,28,0.85)] border-b border-stone-200 dark:border-[rgba(142,218,182,0.15)] sticky top-0 z-10" style={{ top: '52px' }}>
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full sm:w-auto">
            <Button variant="ghost" onClick={handleBack} className="dark:text-[#C7DDD0] text-sm" data-testid="back-btn">
              <ArrowLeft size={16} className="mr-1 sm:mr-2" />
              <span className="hidden xs:inline">Back to Step 5</span>
              <span className="xs:hidden">Back</span>
            </Button>
            <div className="hidden sm:block h-6 w-px bg-stone-200 dark:bg-[rgba(142,218,182,0.15)]" />
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-emerald-600 flex-shrink-0" />
              <h1 className="text-base sm:text-lg font-bold text-stone-800 dark:text-[#EAF7F0]">
                <span className="hidden sm:inline">Step 6: Envelope Setup</span>
                <span className="sm:hidden">Envelope Setup</span>
              </h1>
            </div>
          </div>

          <Button onClick={handleContinue} className="text-white bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto text-sm sm:text-base" data-testid="continue-to-send-btn">
            <span className="hidden sm:inline">Continue to Step 7: Send Mail</span>
            <span className="sm:hidden">Send Mail</span>
            <ArrowRight size={14} className="ml-1 sm:ml-2" />
          </Button>
        </div>

        {/* Delivery Flow Sub-Stepper (Steps 5-7) */}
        <DeliveryFlowStepper currentStep={6} />
      </div>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4 sm:space-y-6 min-w-0 overflow-x-hidden">
        {/* Envelope Navigation */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
          <h3 className="text-lg sm:text-xl font-bold text-stone-800 dark:text-[#EAF7F0]">
            Envelope {currentEnvelopeIndex + 1} of {envelopes.length}
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentEnvelopeIndex === 0} 
              onClick={() => setCurrentEnvelopeIndex(prev => prev - 1)}
              className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"
            >
              <ChevronLeft size={16} />
            </Button>
            {envelopes.map((_, i) => (
              <button 
                key={i} 
                onClick={() => setCurrentEnvelopeIndex(i)}
                className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg text-sm font-medium transition-colors ${
                  i === currentEnvelopeIndex 
                    ? 'bg-emerald-600 text-white' 
                    : 'bg-stone-100 dark:bg-[rgba(10,38,28,0.50)] text-stone-600 dark:text-[#C7DDD0]'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <Button 
              variant="outline" 
              size="sm" 
              disabled={currentEnvelopeIndex === envelopes.length - 1} 
              onClick={() => setCurrentEnvelopeIndex(prev => prev + 1)}
              className="dark:bg-[rgba(10,38,28,0.50)] dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]"
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>

        {/* Envelope Info */}
        <div className="flex items-center gap-3">
          <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400 text-sm px-3 py-1">
            {currentEnvelope?.bureau}
          </Badge>
          <Badge variant="outline" className="dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
            {currentEnvelope?.method?.replace(/_/g, ' ')}
          </Badge>
          {currentEnvelope?.envelope_type === 'data_breach' && (
            <Badge className="bg-amber-100 text-amber-800">Data Breach</Badge>
          )}
        </div>

        {/* Address Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Recipient (Read-Only) */}
          <div className="rounded-xl border p-5 bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
            <div className="flex items-center gap-2 mb-4">
              <Building size={18} className="text-emerald-600 dark:text-emerald-400" />
              <h4 className="font-semibold text-stone-700 dark:text-[#EAF7F0]">To (Recipient)</h4>
              <Badge className="ml-auto bg-stone-200 dark:bg-[rgba(142,218,182,0.15)] text-stone-600 dark:text-[#93AE9F] text-xs">
                Auto-filled
              </Badge>
            </div>
            <div className="text-sm text-stone-600 dark:text-[#C7DDD0] space-y-1">
              <p className="font-medium">{recipient?.name || currentEnvelope?.bureau}</p>
              {recipient?.company && recipient.company !== recipient.name && <p>{recipient.company}</p>}
              <p>{recipient?.address_line_1}</p>
              {recipient?.address_line_2 && <p>{recipient.address_line_2}</p>}
              <p>{recipient?.city}, {recipient?.state} {recipient?.postal_code || recipient?.zip}</p>
            </div>
          </div>

          {/* Sender (With Address Options) */}
          <div className="rounded-xl border p-5 bg-white dark:bg-[rgba(10,38,28,0.65)] border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
            <div className="flex items-center gap-2 mb-4">
              <MapPin size={18} className="text-emerald-600 dark:text-emerald-400" />
              <h4 className="font-semibold text-stone-700 dark:text-[#EAF7F0]">From (Return Address)</h4>
            </div>
            
            {/* ============================================ */}
            {/* USE AN ADDRESS ON FILE - SELECTABLE CARDS */}
            {/* ============================================ */}
            {addressOptions.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-3">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span className="text-sm font-medium text-stone-700 dark:text-[#EAF7F0]">Use an address on file</span>
                </div>
                
                <div className="space-y-2">
                  {addressOptions.map((addr) => {
                    const isSelected = selectedAddressId === addr.id;
                    const resolvedZip = getResolvedPostalCode(addr);
                    const hasIncompleteAddress =
                      addr.isIncomplete ||
                      !addr.state ||
                      !hasValidPostalCode({ ...addr, zip: resolvedZip, postal_code: resolvedZip });
                    return (
                      <div
                        key={addr.id}
                        data-testid={`address-card-${addr.id}`}
                        className={`relative rounded-lg border-2 p-3 transition-all cursor-pointer ${
                          isSelected 
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-[rgba(16,185,129,0.10)]' 
                            : hasIncompleteAddress
                              ? 'border-amber-300 bg-amber-50/50 dark:bg-[rgba(217,119,6,0.10)] hover:border-amber-400'
                              : 'border-stone-200 dark:border-[rgba(142,218,182,0.15)] hover:border-emerald-300'
                        }`}
                        onClick={() => handleUseAddress(addr)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-semibold text-stone-800 dark:text-[#EAF7F0] text-sm">
                              {addr.address_line_1} {addr.address_line_2 && addr.address_line_2}
                            </p>
                            <p className="text-stone-600 dark:text-[#C7DDD0] text-sm">
                              {formatCityStateZip(addr)}
                            </p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <Badge className="bg-stone-100 dark:bg-[rgba(142,218,182,0.15)] text-stone-600 dark:text-[#93AE9F] text-xs">
                                {addr.label}
                              </Badge>
                              {hasIncompleteAddress && !isSelected && (
                                <Badge className="bg-amber-100 text-amber-700 dark:bg-[rgba(217,119,6,0.20)] dark:text-amber-400 text-xs flex items-center gap-1">
                                  <AlertCircle size={10} /> Missing {!addr.state ? 'State' : ''}{!addr.state && !resolvedZip ? ' & ' : ''}{!resolvedZip ? 'ZIP' : ''}
                                </Badge>
                              )}
                              {isSelected && (
                                <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400 text-xs flex items-center gap-1">
                                  <Check size={10} /> Selected
                                </Badge>
                              )}
                            </div>
                            {hasIncompleteAddress && isSelected && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 mt-2 flex items-center gap-1">
                                <AlertCircle size={12} />
                                Please complete the missing {!addr.state ? 'state' : ''}{!addr.state && !resolvedZip ? ' and ' : ''}{!resolvedZip ? 'ZIP code' : ''} below
                              </p>
                            )}
                          </div>
                          
                          {isSelected ? (
                            <div className="bg-emerald-600 text-white px-3 py-1 rounded-md text-xs font-medium">
                              Selected
                            </div>
                          ) : (
                            <Button 
                              size="sm" 
                              variant="outline"
                              className="text-xs h-7"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUseAddress(addr);
                              }}
                            >
                              Use This Address
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Divider */}
            {addressOptions.length > 0 && (
              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-stone-200 dark:border-[rgba(142,218,182,0.15)]"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white dark:bg-[rgba(10,38,28,0.65)] text-stone-500 dark:text-[#93AE9F]">
                    Or enter a new correct address manually
                  </span>
                </div>
              </div>
            )}
            
            {/* Manual Entry Form */}
            <div className="text-xs text-stone-500 dark:text-[#93AE9F] mb-2">
              Enter the complete correct mailing address.
            </div>
            <div className="space-y-3">
              <input 
                type="text" 
                placeholder="Street Address" 
                value={currentSender.address_line_1 || ''} 
                onChange={(e) => handleSenderChange('address_line_1', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-800 dark:text-[#EAF7F0] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="sender-street-input"
              />
              <input 
                type="text" 
                placeholder="Apt, Suite, Unit, Floor (optional)" 
                value={currentSender.address_line_2 || ''} 
                onChange={(e) => handleSenderChange('address_line_2', e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-800 dark:text-[#EAF7F0] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="sender-apt-input"
              />
              <div className="grid grid-cols-1 xs:grid-cols-3 gap-2">
                <input 
                  type="text" 
                  placeholder="City" 
                  value={currentSender.city || ''} 
                  onChange={(e) => handleSenderChange('city', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-800 dark:text-[#EAF7F0] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="sender-city-input"
                />
                <select
                  value={currentSender.state || ''}
                  onChange={(e) => handleSenderChange('state', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-800 dark:text-[#EAF7F0] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="sender-state-input"
                >
                  <option value="">State</option>
                  {Object.entries(STATE_ABBREVS).map(([name, abbr]) => (
                    <option key={abbr} value={abbr}>{abbr}</option>
                  ))}
                </select>
                <input 
                  type="text" 
                  placeholder="ZIP Code" 
                  maxLength={10} 
                  value={getResolvedPostalCode(currentSender) || ''} 
                  onChange={(e) => handleSenderChange('postal_code', e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-stone-200 dark:border-[rgba(142,218,182,0.20)] bg-white dark:bg-[rgba(10,38,28,0.50)] text-stone-800 dark:text-[#EAF7F0] text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  data-testid="sender-zip-input"
                />
              </div>
            </div>
            
            {/* Address Validation Status */}
            {currentSender.address_line_1 &&
              currentSender.city &&
              currentSender.state &&
              hasValidPostalCode(currentSender) && (
              <div className="mt-4 p-3 rounded-lg bg-emerald-50 dark:bg-[rgba(16,185,129,0.10)] border border-emerald-200 dark:border-emerald-500/30">
                <div className="flex items-center gap-2">
                  <Check size={16} className="text-emerald-600" />
                  <span className="text-sm font-medium text-emerald-800 dark:text-emerald-400">Address Selected</span>
                </div>
                <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">
                  {currentSender.address_line_1}
                  {currentSender.address_line_2 ? ` ${currentSender.address_line_2}` : ''}, {formatCityStateZip(currentSender)}
                </p>
                <Badge className="mt-1 bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 text-xs">
                  Source: {currentSender.source === 'identity_anchor' ? 'Client Profile' : currentSender.source === 'profile' ? 'User Profile' : 'Manual Entry'}
                </Badge>
              </div>
            )}
            
            {/* Missing Fields Warning */}
            {(!currentSender.address_line_1 ||
              !currentSender.city ||
              !currentSender.state ||
              !hasValidPostalCode(currentSender)) && (
              <div className="mt-4 p-3 rounded-lg bg-amber-50 dark:bg-[rgba(245,158,11,0.10)] border border-amber-200 dark:border-amber-500/30">
                <div className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-600" />
                  <span className="text-sm font-medium text-amber-800 dark:text-amber-400">Please complete your return address</span>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  Missing: {[
                    !currentSender.address_line_1 && 'Street Address',
                    !currentSender.city && 'City',
                    !currentSender.state && 'State',
                    !hasValidPostalCode(currentSender) && 'ZIP Code'
                  ].filter(Boolean).join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Apply to all toggle */}
        {envelopes.length > 1 && (
          <div className="rounded-xl border p-4 bg-stone-50 dark:bg-[rgba(10,38,28,0.40)] border-stone-200 dark:border-[rgba(142,218,182,0.15)]">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-stone-700 dark:text-[#C7DDD0]">Use this return address for all {envelopes.length} envelopes</span>
              <button 
                onClick={() => setApplyToAll(!applyToAll)} 
                className="p-1" 
                data-testid="apply-to-all-toggle"
              >
                {applyToAll ? (
                  <ToggleRight size={28} className="text-emerald-500" />
                ) : (
                  <ToggleLeft size={28} className="text-stone-400 dark:text-[#93AE9F]" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Envelope Contents Summary */}
        <div className="rounded-xl border p-4 bg-emerald-50/50 dark:bg-[rgba(16,185,129,0.08)] border-emerald-200/50 dark:border-[rgba(16,185,129,0.15)]">
          <h4 className="font-semibold text-stone-700 dark:text-[#EAF7F0] mb-3">Envelope Contents</h4>
          <div className="flex flex-wrap gap-2">
            <Badge variant="outline" className="dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
              {currentEnvelope?.letters?.length || 1} Letter(s)
            </Badge>
            <Badge variant="outline" className="dark:border-[rgba(142,218,182,0.20)] dark:text-[#C7DDD0]">
              ~{currentEnvelope?.page_count || 2} Pages
            </Badge>
            {packageIdentitySupport?.proof_of_address && (
              <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400">
                + Proof of Address
              </Badge>
            )}
            {packageIdentitySupport?.government_id && (
              <Badge className="bg-emerald-100 dark:bg-[rgba(16,185,129,0.20)] text-emerald-700 dark:text-emerald-400">
                + Government ID
              </Badge>
            )}
            {currentEnvelope?.envelope_type === 'data_breach' && dataBreachProof && (
              <Badge className="bg-amber-100 text-amber-800">
                + Data Breach Proof
              </Badge>
            )}
          </div>
        </div>

        {/* Address Validation Info */}
        {(!currentSender.full_name ||
          !currentSender.address_line_1 ||
          !currentSender.city ||
          !currentSender.state ||
          !hasValidPostalCode(currentSender)) && (
          <div className="rounded-xl border border-amber-300 dark:border-[rgba(245,158,11,0.30)] bg-amber-50 dark:bg-[rgba(245,158,11,0.08)] p-4">
            <div className="flex items-start gap-3">
              <AlertCircle size={20} className="text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800 dark:text-amber-300">
                  Please complete your return address
                </p>
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                  All fields are required for mailing. Your return address ensures you receive any responses.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryFlowStep7;
